// deps
import React, { memo, useCallback, useMemo, useRef } from "react";

// internal-deps
import { closeWebview, useCustomHardwareBack, DineoutCheckoutRoute } from "@swiggy-private/checkout-core";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { format, isEmpty } from "@swiggy-private/js-utils";

// types
import { CartUIWrapperProps } from "./types";

// constants
import { HEADER_BG_COLOR, STEAL_STORE_CART_TEST_IDS, TEXTS } from "./constants";

// components
import { CommonHeader, CoreLayoutV2 } from "@swiggy-private/core-ui";
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { Shimmer } from "../../shimmer";

// styles
import { HeaderContainer, ContentContainer, HeaderTitle } from "./styles";

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

function StealStoreCartUIWrapper({ children, ...props }: CartUIWrapperProps): React.ReactElement {
    const { containerId, isLoading } = props;
    const containerRef = useRef<ScrollableDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const content = useMemo(() => (isLoading ? <Shimmer /> : children), [isLoading, children]);

    const onBackHandler = useCallback(() => {
        props.onBack?.();
        closeWebview();
    }, [props.onBack]);

    useCustomHardwareBack([DineoutCheckoutRoute.STEAL_STORE_CART], onBackHandler);

    return (
        <CoreLayoutV2>
            <>
                <CoreLayoutV2.Header
                    dataTestId={COMMON_HEADER_TEST_IDS.CART_HEADER_PARENT}
                    containerRef={headerRef}
                    containerStyleProps={{
                        backgroundColor: HEADER_BG_COLOR,
                    }}
                >
                    <CommonHeader onBack={onBackHandler} isVisible={true} marginTop={0} dataTestId={STEAL_STORE_CART_TEST_IDS.CART_HEADER}>
                        <HeaderContainer>
                            <HeaderTitle
                                variant="V2.body3.neutral.medium"
                                color="text_Highest_Emphasis"
                                isLoading={isLoading}
                                data-testid={STEAL_STORE_CART_TEST_IDS.HEADER_TITLE}
                            >
                                {TEXTS.HEADER_TITLE}
                            </HeaderTitle>
                            {!isEmpty(props?.totalItemsCount) && (
                                <Text
                                    variant="V2.body3.neutral.regular"
                                    color="text_Low_Emphasis"
                                    data-testid={STEAL_STORE_CART_TEST_IDS.TOTAL_ITEMS_COUNT}
                                >
                                    {format(TEXTS.HEADER_SUBTITLE, props?.totalItemsCount)}
                                </Text>
                            )}
                        </HeaderContainer>
                    </CommonHeader>
                </CoreLayoutV2.Header>
                <CoreLayoutV2.Content ref={containerRef} headerRef={headerRef} contentId={containerId}>
                    <ContentContainer>{content}</ContentContainer>
                </CoreLayoutV2.Content>
            </>
        </CoreLayoutV2>
    );
}

export default memo(StealStoreCartUIWrapper);
